import type { UseQueryOptions } from '@core/types'
import { toValue, useCoreConfig, useQuery, useStrapi, type MaybeRefOrGetter } from '#imports'
import type { components as StrapiComponents } from '@core/types/clients/strapi'

export const useStrapiBlog = () => {
    const { site } = useCoreConfig()
    const strapi = useStrapi()

    /**
     * Returns a list of all blog posts.
     */
    function getBlogs(
        options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}
    ) {
        const { enabled = true, lazy = false, server = false } = options

        const query = useQuery(async () => {
            const { data } = await strapi.GET('/blogs', {
                params: {
                    query: {
                        brand: site.brand
                    }
                },
            })

            return data as StrapiComponents['schemas']['Blog'][]
        }, {
            key: 'strapi:blogs',
            lazy,
            server,
            enabled,
        })

        return query
    }

    /**
     * Returns a single blog post based on a slug.
     */
    function getBlogBySlug(
        slug: MaybeRefOrGetter<string>,
        options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}
    ) {
        const { enabled = true, lazy = false, server = false } = options
        const key = () => `blog:blogBySlug-${toValue(slug) || 'none'}`

        const query = useQuery(async () => {
            const { data } = await strapi.GET('/blogs/{slug}', {
                params: {
                    path: {
                        slug: toValue(slug)
                    },
                    query: {
                        brand: site.brand
                    },
                },
            })

            return data as StrapiComponents['schemas']['Blog']
        },
        {
            key,
            immediate: true,
            lazy,
            server,
            enabled: () => !!enabled && !!toValue(slug)?.length
        })

        return query
    }

    return {
        getBlogs,
        getBlogBySlug,
    }
}
